import { Injectable } from '@angular/core';
import { EnhancedDatasetResult, FeatureFlag, FeatureFlagsFacade } from '@mode/shared/contract-common';
import { isPresent, naturalSort } from '@mode/shared/util-js';
import { SwitchViewActions, SwitchViewReorderingFacade } from '@mode/switch-views/contract';
import { SwitchViewSortAttributeTypes } from 'libs/shared/contract-common/src/lib/switch/switch-view.types';
import { BehaviorSubject, combineLatest, distinctUntilChanged, map, Observable, shareReplay, Subject } from 'rxjs';
import { DomainAppState } from '../switch-view-domain.types';
import { Store } from '@ngrx/store';
import { getDirectChildElement, isDragBeforeElement } from '@mode/shared/util-js';
import { getEnhancedDatasetResults } from './switch-view.selectors';

/**
 * SwitchViewReorderingService provides the re-ordering functionality
 */

@Injectable({ providedIn: 'root' })
// eslint-disable-next-line @nx/workspace/no-facade-provided-in-root
export class SwitchViewReorderingService implements SwitchViewReorderingFacade {
  private _isReorderingEnabled: Observable<boolean>;
  private _dropIndex = new BehaviorSubject(-1);
  private _currentDatasetDragIndex = new BehaviorSubject(-1);
  private _dragOverStream = new Subject<DragEvent>();
  private readonly SWITCH_VIEW_DRAG_DATA = 'switch-view-drag-item';

  constructor(private store: Store<DomainAppState>, private featureFlagService: FeatureFlagsFacade) {
    this._isReorderingEnabled = this.featureFlagService.asObservable(FeatureFlag.ReorderingDatasets);
  }

  // selector built on top of the getEnhancedDatasetResults, sorts the list based on the sort attribute
  get sortedEnhancedDatasetResults$(): Observable<EnhancedDatasetResult[]> {
    return combineLatest([this.store.select(getEnhancedDatasetResults), this.isReorderingEnabled$]).pipe(
      map(([enhancedDatasetResults, isReorderingEnabled]) => {
        if (!isReorderingEnabled) {
          return this.naturalSortDatasets(enhancedDatasetResults, SwitchViewSortAttributeTypes.DisplayName);
        } else {
          return this.partitionAndSortDatasets(enhancedDatasetResults);
        }
      }),
      shareReplay(1)
    );
  }

  get isReorderingEnabled$() {
    return this._isReorderingEnabled;
  }

  get dropIndex$() {
    return this._dropIndex.asObservable().pipe(distinctUntilChanged(), shareReplay(1));
  }

  get currentDatasetDragIndex$() {
    return this._currentDatasetDragIndex.asObservable().pipe(distinctUntilChanged(), shareReplay(1));
  }

  get dragOverStream$() {
    return this._dragOverStream.asObservable();
  }

  private get dropIndex() {
    return this._dropIndex.value;
  }

  private set dropIndex(index: number) {
    this._dropIndex.next(index);
  }

  private get currentDatasetDragIndex() {
    return this._currentDatasetDragIndex.value;
  }

  private set currentDatasetDragIndex(index: number) {
    this._currentDatasetDragIndex.next(index);
  }

  pushToDragOverStream($event: DragEvent) {
    this._dragOverStream.next($event);
  }

  onMoveDatasetUp(reportToken: string, enhancedDatasetResults: EnhancedDatasetResult[], pos: number) {
    return this.reorderDatasets(reportToken, enhancedDatasetResults, pos, pos - 1);
  }

  onMoveDatasetDown(reportToken: string, enhancedDatasetResults: EnhancedDatasetResult[], pos: number) {
    return this.reorderDatasets(reportToken, enhancedDatasetResults, pos, pos + 1);
  }

  onMoveDatasetToTop(reportToken: string, enhancedDatasetResults: EnhancedDatasetResult[], pos: number) {
    return this.reorderDatasets(reportToken, enhancedDatasetResults, pos, 0);
  }

  onMoveDatasetToBottom(reportToken: string, enhancedDatasetResults: EnhancedDatasetResult[], pos: number) {
    return this.reorderDatasets(reportToken, enhancedDatasetResults, pos, enhancedDatasetResults.length - 1);
  }

  calculateDropIndexFromDragEvent($event: DragEvent, dropZone: HTMLElement, indexAttribute: string) {
    const directChild = getDirectChildElement(dropZone, $event.target as Element);
    const index = directChild?.getAttribute(indexAttribute);
    if (index && directChild) {
      const dropIndex = isDragBeforeElement($event, directChild) ? parseFloat(index) : parseFloat(index) + 1;
      this.dropIndex = dropIndex;
    }
  }

  reorderDatasets(
    reportToken: string,
    originalEnhancedDatasetResults: EnhancedDatasetResult[],
    currentIndex: number,
    newIndex: number
  ) {
    if (newIndex < 0 || newIndex > originalEnhancedDatasetResults.length - 1 || currentIndex === newIndex)
      return [...originalEnhancedDatasetResults];
    const enhancedDatasetResults = [...originalEnhancedDatasetResults]; // create a new reference to the datasets array
    const [movedDataset] = enhancedDatasetResults.splice(currentIndex, 1);
    enhancedDatasetResults.splice(newIndex, 0, movedDataset);
    const previousIndex = this.findPreviousIndex(enhancedDatasetResults, newIndex);
    const prevSwitchViewToken = enhancedDatasetResults[previousIndex]?.switchViewToken || null;
    this.dispatchReorderAction(reportToken, movedDataset.switchViewToken, prevSwitchViewToken);
    return enhancedDatasetResults;
  }

  // drag and drop actions
  onDatasetDragStart($event: DragEvent, pos: number) {
    $event.dataTransfer?.setData('application/x-custom-drag-data', this.SWITCH_VIEW_DRAG_DATA);
    $event.stopPropagation();
    this.currentDatasetDragIndex = pos;
  }

  onDatasetDragEnd($event: DragEvent, pos: number) {
    $event.stopPropagation();
    this.resetDropIndex();
  }

  onDatasetDragEnter($event: DragEvent) {
    $event.stopPropagation();
    if (this.currentDatasetDragIndex >= 0) {
      this.pushToDragOverStream($event);
    }
  }

  onDatasetDragOver($event: DragEvent) {
    if (this.currentDatasetDragIndex >= 0) {
      $event.preventDefault();
      this.pushToDragOverStream($event);
    }
  }

  onDatasetDragLeave($event: DragEvent) {
    $event.stopPropagation();
  }

  onDatasetDrop(reportToken: string, $event: DragEvent, enhancedDatasetResults: EnhancedDatasetResult[]) {
    const dropIndex = this.dropIndex;
    const currentDatasetDragIndex = this.currentDatasetDragIndex;
    this.resetDropIndex();

    if ($event.dataTransfer?.getData('application/x-custom-drag-data') == this.SWITCH_VIEW_DRAG_DATA) {
      let adjustedDropIndex: number;
      adjustedDropIndex = dropIndex > enhancedDatasetResults.length ? enhancedDatasetResults.length : dropIndex;
      adjustedDropIndex = currentDatasetDragIndex < adjustedDropIndex ? adjustedDropIndex - 1 : adjustedDropIndex;
      return this.reorderDatasets(reportToken, enhancedDatasetResults, currentDatasetDragIndex, adjustedDropIndex);
    } else {
      return [...enhancedDatasetResults];
    }
  }

  onDragOverNonDraggableDropZone($event: DragEvent, enhancedDatasetResults: EnhancedDatasetResult[]) {
    $event.preventDefault();
    this.dropIndex = enhancedDatasetResults.length - 1;
  }

  partitionAndSortDatasets(enhancedDatasetResults: EnhancedDatasetResult[]) {
    const rankedDatasets: EnhancedDatasetResult[] = [];
    const unrankedDatasets: EnhancedDatasetResult[] = [];

    for (const dataset of enhancedDatasetResults) {
      if (isPresent(dataset.rowOrder)) {
        rankedDatasets.push(dataset);
      } else {
        unrankedDatasets.push(dataset);
      }
    }

    const sortedRunQueries = this.naturalSortDatasets(rankedDatasets, SwitchViewSortAttributeTypes.RowOrder);

    const partitionedDatasets = [...sortedRunQueries, ...unrankedDatasets];
    return partitionedDatasets;
  }

  naturalSortDatasets(datasets: EnhancedDatasetResult[], sortAttr: SwitchViewSortAttributeTypes) {
    return [...datasets].sort((a, b) => naturalSort(a[sortAttr], b[sortAttr]));
  }

  findPreviousIndex(enhancedDatasetResults: EnhancedDatasetResult[], newIndex: number) {
    const firstUnrankedIndex = enhancedDatasetResults.findIndex((dataset) => !isPresent(dataset.rowOrder));
    const lastRankedIndex = firstUnrankedIndex === -1 ? enhancedDatasetResults.length - 1 : firstUnrankedIndex - 1;
    return newIndex > lastRankedIndex ? lastRankedIndex : newIndex - 1;
  }

  resetDropIndex() {
    this.currentDatasetDragIndex = -1;
    this.dropIndex = -1;
  }

  dispatchReorderAction(reportToken: string, switchViewToken: string, prevSwitchViewToken: string | null) {
    this.store.dispatch(
      SwitchViewActions.reorderDatasets({
        reportToken,
        switchViewToken,
        prevSwitchViewToken,
      })
    );
  }
}
